import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Link, useParams, NavLink } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { BottomTabs } from "@/components/BottomTabs";
import { StatusBadge } from "@/components/StatusBadge";
import LoadError from "@/components/LoadError";
import { api } from "@/lib/api";
import { formatRange, mondayOf, statusLabel, todayPT, entryHours, phaseSplit } from "@/lib/util";
import { usePhases } from "@/lib/phases";
import dayjs from "dayjs";
import { ClipboardList, Megaphone, Check, X, RotateCcw, MessageSquare, Grid3x3, ShieldCheck, CalendarDays } from "lucide-react";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import ForemanSummary from "@/pages/foreman/ForemanSummary";
import { ThisWeek as MyHoursWeek } from "@/pages/crew/CrewApp";
import SafetyMeetings from "@/pages/safety/SafetyMeetings";
import SafetyMeetingReport from "@/pages/safety/SafetyMeetingReport";
import SafetyComplianceRollup from "@/pages/safety/SafetyComplianceRollup";
import HoursByProjectReport from "@/pages/HoursByProjectReport";


// Six pills asked a foreman to distinguish things that make no difference to
// them. "Needs correction" and "Rejected" both mean "I sent it back and it is
// with them now". "Draft" is just "not sent yet", which is what everything in
// that group already is. "Exported" is the office's business -- a foreman does
// not export anything.
//
// So three groups, matching the only three decisions a foreman actually makes:
// act on it, wait for someone, or leave it alone. The exact status still shows
// on each row's badge, which is where that detail belongs -- on the one card it
// describes, not as a filter you have to choose between first.
const FILTER_GROUPS = [
    {
        id: "waiting_on_you",
        label: "Waiting on you",
        statuses: ["submitted"],
        help: "Sent in and certified by the crew member. This is your list.",
    },
    {
        id: "waiting_on_crew",
        label: "Waiting on crew",
        statuses: ["draft", "needs_correction", "rejected"],
        help: "With the crew member — either not sent yet, or sent back by you. Nothing here is yours to approve until they send it.",
    },
    {
        id: "done",
        label: "Done",
        statuses: ["approved", "exported"],
        help: "Approved, and on its way to payroll or already there. Nothing to do.",
    },
];

const GROUP_BY_ID = Object.fromEntries(FILTER_GROUPS.map((g) => [g.id, g]));

function ApprovalsQueue() {
    const [all, setAll] = useState([]);
    const [missing, setMissing] = useState([]);
    const [filter, setFilter] = useState("waiting_on_you");
    const [week, setWeek] = useState(mondayOf(todayPT()));
    const [err, setErr] = useState(null);
    const [loaded, setLoaded] = useState(false);

    // One request for the whole week, then count and filter here. Fetching per
    // status meant a round trip on every chip tap and no way to show counts --
    // and it listed every week at once, so the same person appeared three times
    // with three different dates and no clue which mattered.
    const load = async () => {
        setErr(null);
        try {
            const [a, b] = await Promise.all([
                api.get(`/timecards?week=${week}`),
                api.get(`/timecards/missing?week=${week}`),
            ]);
            setAll(a.data);
            setMissing(b.data);
            setLoaded(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load the approval queue.");
        }
    };
    useEffect(() => { load(); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = all.filter((r) => GROUP_BY_ID[filter].statuses.includes(r.status));
    const countOf = (g) => all.filter((r) => g.statuses.includes(r.status)).length;

    // Without this the queue rendered as "No timecards in this status." on a
    // failed request -- a foreman would read that as "nothing to approve".
    if (err && !loaded) return <LoadError message={err} onRetry={load} testId="approvals-error" />;

    return (
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="approvals-queue">
            {/* One row, each chip carrying its own count. The counts used to be
                four tiles on a separate Stats tab -- a whole screen for four
                numbers that are really just the size of these lists. Put on the
                chips they are the number and the control at once, and the tab
                is gone. Three grouped rows with headings ate the top of a phone
                before a single name appeared. */}
            <div className="space-y-2" data-testid="queue-filters">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-brand-foreman-accent">
                        Week of {dayjs(week).format("MMM D, YYYY")}
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setWeek(mondayOf(dayjs(week).subtract(7, "day").format("YYYY-MM-DD")))}
                            className="px-2 py-1 rounded-lg text-xs font-bold hover:bg-brand-stone" data-testid="queue-prev-week">‹ Prev</button>
                        <input type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))}
                            className="text-xs border border-brand-charcoal/20 rounded-lg px-2 py-1" data-testid="queue-week-picker" />
                        <button onClick={() => setWeek(mondayOf(dayjs(week).add(7, "day").format("YYYY-MM-DD")))}
                            disabled={dayjs(week).add(7, "day").isAfter(todayPT(), "day")}
                            className="px-2 py-1 rounded-lg text-xs font-bold hover:bg-brand-stone disabled:opacity-30" data-testid="queue-next-week">Next ›</button>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                    {FILTER_GROUPS.map((g) => (
                        <button key={g.id} onClick={() => setFilter(g.id)}
                            data-testid={`filter-${g.id}`}
                            title={g.help}
                            className={`px-2 py-2 rounded-xl text-[11px] font-bold uppercase tracking-wider border transition flex flex-col items-center gap-0.5 ${filter === g.id ? "bg-brand-foreman-header text-brand-bone border-brand-foreman-header" : "bg-white text-brand-charcoal border-brand-charcoal/20 hover:border-brand-foreman-header"}`}>
                            <span className="tabular-nums text-lg leading-none" data-testid={`count-${g.id}`}>{countOf(g)}</span>
                            <span className="leading-tight text-center">{g.label}</span>
                        </button>
                    ))}
                </div>
                <p className="text-xs text-brand-steel" data-testid="filter-help">{GROUP_BY_ID[filter].help}</p>
            </div>
            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card divide-y divide-brand-charcoal/10" data-testid="timecards-list">
                {rows.length === 0 && <div className="p-6 text-center text-brand-charcoal/60 text-sm">No timecards in this status.</div>}
                {rows.map((r) => (
                    <Link key={r.timecard_id} to={`/foreman/timecard/${r.timecard_id}`} className="block p-4 hover:bg-brand-stone/60" data-testid={`tc-${r.timecard_id}`}>
                        <div className="flex justify-between items-start gap-3">
                            <div className="min-w-0">
                                <div className="font-bold text-brand-charcoal truncate">{r.employee_name}</div>
                                <div className="text-xs text-brand-charcoal/60">{r.employee_code} · Week of {dayjs(r.week_start).format("MMM D, YYYY")}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-xl text-brand-navy">{(r.totals?.total || 0).toFixed(1)}h</span>
                                <StatusBadge status={r.status} />
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-4" data-testid="missing-list">
                {/* The week picker at the top of the screen drives this list too.
                    Two pickers on one screen, set independently, meant the list
                    and the counts above it could describe different weeks.

                    This is driven by /timecards/missing, which checks
                    submission status only -- someone can have logged real
                    draft hours (visible on This Week / the queue above) and
                    still land here, because they haven't sent the week in
                    yet. "Nothing started yet" used to claim otherwise, which
                    read as a data bug the first time someone with 16 draft
                    hours showed up on it. */}
                <div className="text-[10px] uppercase tracking-widest text-brand-foreman-accent mb-2">
                    Hasn't turned in a timecard yet · Week of {dayjs(week).format("MMM D")}
                </div>
                {missing.length === 0 ? (
                    <div className="text-sm text-brand-charcoal/60">Everyone has turned their week in. Nice.</div>
                ) : (
                    <ul className="space-y-1 text-sm">
                        {missing.map((m) => (
                            <li key={m.employee_id} className="flex justify-between" data-testid={`missing-${m.employee_id}`}>
                                <span>{m.name}</span>
                                <span className="text-brand-charcoal/60">{m.employee_code}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function ReviewSheet() {
    const { id } = useParams();
    const [d, setD] = useState(null);
    const [actionNote, setActionNote] = useState("");
    const [internalNote, setInternalNote] = useState("");
    const [projects, setProjects] = useState([]);
    const { phaseName } = usePhases();

    const [err, setErr] = useState(null);
    const load = () => {
        setErr(null);
        return api.get(`/timecards/${id}`)
            .then((r) => setD(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Couldn’t load this timecard."));
    };
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    /* The timecard payload carries project_id on each entry but not the job
       names -- a foreman reviewing a week needs the name, so the catalog comes
       alongside. A failure here leaves the names blank rather than the sheet,
       because an approval screen that will not load is worse than one that
       shows a dash where "Reddick" should be. */
    useEffect(() => {
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    }, []);
    const projectsById = Object.fromEntries(projects.map((p) => [p.project_id, p]));

    if (err && !d) return <LoadError message={err} onRetry={load} testId="timecard-detail-error" />;
    if (!d) return <div className="p-6 text-brand-charcoal/60">Loading…</div>;
    const { timecard: tc, entries, history, internal_notes, employee, deadline, pay_period, year } = d;

    const act = async (action) => {
        try {
            if ((action === "reject" || action === "sendback") && !actionNote.trim()) {
                alert("A note is required to reject or send back.");
                return;
            }
            if (action === "approve") await api.post(`/timecards/${id}/approve`);
            if (action === "reject") await api.post(`/timecards/${id}/reject`, { note: actionNote });
            if (action === "sendback") await api.post(`/timecards/${id}/sendback`, { note: actionNote });
            setActionNote("");
            await load();
        } catch (e) {
            alert(e?.response?.data?.detail || "Action failed");
        }
    };

    const addInternal = async () => {
        if (!internalNote.trim()) return;
        await api.post(`/timecards/${id}/internal-note`, { note: internalNote });
        setInternalNote("");
        await load();
    };

    const grouped = {};
    entries.forEach((e) => { (grouped[e.date] = grouped[e.date] || []).push(e); });
    const days = Object.keys(grouped).sort();

    return (
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-4 pb-28" data-testid="review-sheet">
            <div className="bg-brand-foreman-header text-brand-bone rounded-2xl p-5 shadow-lift">
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Pay Period {pay_period} of 52 · {year}</div>
                <div className="font-bold text-3xl mt-1">{employee?.name}</div>
                <div className="text-sm text-brand-bone/80">{employee?.employee_code} · Week of {dayjs(tc.week_start).format("MMM D, YYYY")}</div>
                <div className="mt-3"><StatusBadge status={tc.status} /></div>
            </div>

            {/* One week, one number, then where it went. The four boxes that
                used to sit here -- Regular, Overtime, Double-Time, PTO -- are
                categories Talon does not record in this app, and three of them
                had been reading 0.00 out of a totals object that no longer
                carries the keys. What a foreman actually checks against is the
                job: 32 hours on Reddick when the crew was on Waters is the
                error worth catching before payroll. */}
            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5" data-testid="review-totals">
                <div className="flex justify-between items-end">
                    <span className="text-xs uppercase tracking-widest text-brand-charcoal/60">Total hours</span>
                    <span className="font-bold text-3xl text-brand-navy tabular-nums" data-testid="review-total">
                        {(tc.totals?.total || 0).toFixed(2)}
                    </span>
                </div>
                {Object.keys(tc.totals?.by_project || {}).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-brand-charcoal/10 space-y-1">
                        <div className="text-[10px] uppercase tracking-widest text-brand-foreman-accent mb-1">By job</div>
                        {Object.entries(tc.totals.by_project).map(([pid, hrs]) => (
                            <div key={pid} className="flex justify-between text-sm" data-testid={`review-total-project-${pid}`}>
                                <span>{projectsById[pid]?.name || "—"}</span>
                                <span className="tabular-nums font-semibold">{(hrs || 0).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5" data-testid="daily-breakdown">
                <div className="text-[10px] uppercase tracking-widest text-brand-foreman-accent mb-3">Daily breakdown</div>
                {days.length === 0 ? <div className="text-sm text-brand-charcoal/60">No entries.</div> : days.map((d) => (
                    <div key={d} className="py-2 border-b border-brand-charcoal/10 last:border-0">
                        <div className="font-bold text-sm">{dayjs(d).format("dddd, MMM D")}</div>
                        <ul className="ml-3 mt-1 text-sm text-brand-charcoal/80 space-y-1">
                            {/* The job, then the phases with their hours. A foreman
                                approving a week is deciding whether the work
                                claimed matches the work they saw on site, and
                                "8.00 hrs" alone does not let them. The split is
                                the same one the crew member saw and accepted. */}
                            {grouped[d].map((e) => {
                                const proj = projectsById[e.project_id];
                                const split = phaseSplit(e);
                                return (
                                    <li key={e.entry_id} data-testid={`review-entry-${e.entry_id}`}>
                                        <span className="font-semibold text-brand-charcoal">
                                            {proj?.name || "—"}
                                        </span>
                                        <span className="ml-2 tabular-nums">{entryHours(e).toFixed(2)}h</span>
                                        <span className="block ml-3 text-xs">
                                            {(e.phase_numbers || []).map((n) => (
                                                <span key={n} className="inline-block mr-3 whitespace-nowrap">
                                                    <span className="tabular-nums text-brand-charcoal/45">{n}</span>{" "}
                                                    {phaseName(n)}{" "}
                                                    <span className="tabular-nums font-semibold">{(split[n] ?? 0).toFixed(2)}</span>
                                                </span>
                                            ))}
                                        </span>
                                        {e.notes && <span className="block ml-3 italic text-xs">"{e.notes}"</span>}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </div>

            {["submitted", "needs_correction", "rejected"].includes(tc.status) && (
                <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5 space-y-3" data-testid="actions-panel">
                    <div className="text-[10px] uppercase tracking-widest text-brand-foreman-accent">Actions</div>
                    <textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)} rows="2" placeholder="Note to worker (required to reject or send back)"
                        data-testid="action-note"
                        className="w-full border border-brand-charcoal/20 rounded-xl p-3 text-sm focus:ring-2 focus:ring-brand-foreman-header outline-none" />
                    <div className="grid grid-cols-3 gap-2">
                        <button onClick={() => act("approve")} className="h-12 rounded-xl bg-brand-success hover:bg-brand-success text-white font-bold flex items-center justify-center gap-1" data-testid="approve-button"><Check className="h-4 w-4" />Approve</button>
                        <button onClick={() => act("sendback")} className="h-12 rounded-xl bg-brand-warn hover:bg-brand-gold-dark text-white font-bold flex items-center justify-center gap-1" data-testid="sendback-button"><RotateCcw className="h-4 w-4" />Send back</button>
                        <button onClick={() => act("reject")} className="h-12 rounded-xl bg-brand-danger hover:bg-brand-danger/85 text-white font-bold flex items-center justify-center gap-1" data-testid="reject-button"><X className="h-4 w-4" />Reject</button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5" data-testid="internal-notes">
                <div className="text-[10px] uppercase tracking-widest text-brand-foreman-accent mb-2">Internal notes (private)</div>
                <ul className="space-y-2 text-sm">
                    {internal_notes.map((n) => (
                        <li key={n.note_id} className="bg-brand-stone rounded-lg p-2">
                            <div className="text-xs text-brand-charcoal/60">{n.author_name} · {dayjs(n.created_at).format("MMM D, h:mm A")}</div>
                            <div>{n.note}</div>
                        </li>
                    ))}
                </ul>
                <div className="flex gap-2 mt-3">
                    <input value={internalNote} onChange={(e) => setInternalNote(e.target.value)} placeholder="Add an internal note…"
                        data-testid="internal-note-input"
                        className="flex-1 h-11 border border-brand-charcoal/20 rounded-xl px-3 focus:ring-2 focus:ring-brand-foreman-header outline-none" />
                    <button onClick={addInternal} className="h-11 px-4 rounded-xl bg-brand-charcoal text-brand-bone font-bold" data-testid="add-internal-note"><MessageSquare className="h-4 w-4" /></button>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5" data-testid="audit-history">
                <div className="text-[10px] uppercase tracking-widest text-brand-foreman-accent mb-2">Audit history</div>
                <ul className="space-y-2 text-sm">
                    {history.map((h) => (
                        <li key={h.history_id} className="flex flex-col">
                            <span><b className="capitalize">{h.action.replace("_", " ")}</b> by {h.by_name} · <span className="text-brand-charcoal/60">{dayjs(h.created_at).format("MMM D, h:mm A")}</span></span>
                            {h.note && <span className="text-xs italic text-brand-charcoal/70">"{h.note}"</span>}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default function ForemanApp() {
    return (
        <div className="min-h-screen paper-bg flex flex-col">
            <AppHeader />
            <main className="flex-1">
                <Routes>
                    <Route index element={<ForemanSummary />} />
                    <Route path="reports/hours-by-project" element={<HoursByProjectReport />} />
                    {/* A foreman's OWN hours. They work on the jobs they run,
                        and until this existed the app had no way for them to log
                        a day -- every foreman screen is about other people's
                        weeks. It mounts the crew week screen rather than a
                        second entry UI, so the two cannot drift apart. */}
                    <Route path="my-hours" element={<MyHoursWeek />} />
                    <Route path="approvals" element={<ApprovalsQueue />} />
                    <Route path="timecard/:id" element={<ReviewSheet />} />
                    <Route path="safety" element={<SafetyMeetings canViewReport />} />
                    <Route path="safety/compliance-report" element={<SafetyComplianceRollup />} />
                    <Route path="safety/:meetingId/report" element={<SafetyMeetingReport />} />
                    <Route path="announcements" element={<AnnouncementsPage />} />
                </Routes>
            </main>
            <BottomTabs
                tabs={[
                    // "Summary" sounded like a report you read. This is where
                    // the week's work actually gets done, so it says so.
                    { to: "/foreman", label: "This Week", icon: Grid3x3, end: true },
                    { to: "/foreman/my-hours", label: "My Hours", icon: CalendarDays },
                    { to: "/foreman/approvals", label: "Queue", icon: ClipboardList },
                    { to: "/foreman/safety", label: "Safety", icon: ShieldCheck },
                    { to: "/foreman/announcements", label: "News", icon: Megaphone },
                ]}
            />
        </div>
    );
}
